import { ApiClient, AppError } from "./api";
import { ENV } from "./env";
import { resolveTelegramMiniAppInitialRoute } from "../../shared/telegram-platform";
import {
  flowStatusLabel,
  formatAmountValue,
  formatMoneyLine,
  formatTimestamp,
  normalizeAmountInput,
  sanitizeAmountInput,
  statusTone,
  submissionStatusLabel,
  validateAmount,
} from "./format";
import { applyTheme } from "./theme";
import {
  getTelegramWebApp,
  haptic,
  initializeTelegram,
  openBotLink,
  resolveLaunchToken,
  syncTelegramBackButton,
  syncTelegramChrome,
} from "./telegram";
import type {
  AppRoute,
  AuthData,
  BootstrapData,
  ColorScheme,
  Currency,
  Flow,
  FlowSummary,
  HistoryItem,
  Submission,
  SubmissionStatus,
  TelegramThemeParams,
} from "./types";
import "./styles.css";

type NoticeTone = "success" | "warning" | "danger" | "neutral";
type AuthState = "loading" | "ready" | "error";
type BusyState = "idle" | "refreshing" | "saving" | "confirming" | "retrying" | "cancelling";

interface AppNotice {
  text: string;
  tone: NoticeTone;
}

interface FlowDraft {
  amount: string;
  currency: Currency | null;
  error: string;
}

interface AppState {
  accessToken: string | null;
  activeFlow: Flow | null;
  authError: string | null;
  authState: AuthState;
  busy: BusyState;
  configCurrencies: Currency[];
  currentSubmission: Submission | null;
  draft: FlowDraft;
  historyItems: HistoryItem[];
  historyNextCursor: string | null;
  isHistoryLoaded: boolean;
  isHistoryLoading: boolean;
  isSubmissionLoading: boolean;
  isSummaryLoading: boolean;
  notice: AppNotice | null;
  recentSubmission: HistoryItem | null;
  route: AppRoute;
  submissionCache: Record<string, Submission>;
  submissionError: string | null;
  summary: FlowSummary | null;
  summaryError: string | null;
  summaryFlowId: string | null;
  userDisplayName: string;
}

const rootElement = document.querySelector<HTMLDivElement>("#app");

if (!rootElement) {
  throw new Error("Missing #app root");
}

const root: HTMLDivElement = rootElement;

const api = new ApiClient();
const tg = getTelegramWebApp();

const state: AppState = {
  accessToken: null,
  activeFlow: null,
  authError: null,
  authState: "loading",
  busy: "idle",
  configCurrencies: ["CNY", "USD", "USDT"],
  currentSubmission: null,
  draft: {
    amount: "",
    currency: null,
    error: "",
  },
  historyItems: [],
  historyNextCursor: null,
  isHistoryLoaded: false,
  isHistoryLoading: false,
  isSubmissionLoading: false,
  isSummaryLoading: false,
  notice: ENV.configWarning ? { text: ENV.configWarning, tone: "warning" } : null,
  recentSubmission: null,
  route: parseRoute(window.location.pathname),
  submissionCache: {},
  submissionError: null,
  summary: null,
  summaryError: null,
  summaryFlowId: null,
  userDisplayName: "Telegram 用户",
};

function parseRoute(pathname: string): AppRoute {
  const cleaned = pathname.replace(/\/+$/u, "") || "/";

  if (cleaned === "/") {
    return { name: "home" };
  }

  if (cleaned === "/flow") {
    return { name: "flow" };
  }

  if (cleaned === "/history") {
    return { name: "history" };
  }

  const confirmMatch = cleaned.match(/^\/flow\/([^/]+)\/confirm$/u);
  if (confirmMatch) {
    return { name: "confirm", flowId: decodeURIComponent(confirmMatch[1]) };
  }

  const resultMatch = cleaned.match(/^\/result\/([^/]+)$/u);
  if (resultMatch) {
    return { name: "result", submissionId: decodeURIComponent(resultMatch[1]) };
  }

  const detailMatch = cleaned.match(/^\/submission\/([^/]+)$/u);
  if (detailMatch) {
    return { name: "detail", submissionId: decodeURIComponent(detailMatch[1]) };
  }

  return { name: "home" };
}

function hrefForRoute(route: AppRoute): string {
  switch (route.name) {
    case "home":
      return "/";
    case "flow":
      return "/flow";
    case "history":
      return "/history";
    case "confirm":
      return route.flowId ? `/flow/${encodeURIComponent(route.flowId)}/confirm` : "/flow";
    case "result":
      return route.submissionId ? `/result/${encodeURIComponent(route.submissionId)}` : "/";
    case "detail":
      return `/submission/${encodeURIComponent(route.submissionId)}`;
    default:
      return "/";
  }
}

function titleForRoute(route: AppRoute): string {
  switch (route.name) {
    case "home":
      return "当前流程";
    case "flow":
      return "填写金额";
    case "confirm":
      return "确认提交";
    case "result":
      return "提交状态";
    case "history":
      return "历史记录";
    case "detail":
      return "记录详情";
    default:
      return "金额提交流程";
  }
}

function routeNeedsBack(route: AppRoute): boolean {
  return route.name !== "home";
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function setNotice(tone: NoticeTone, text: string): void {
  state.notice = { text, tone };
}

function clearNotice(): void {
  if (ENV.configWarning) {
    state.notice = { text: ENV.configWarning, tone: "warning" };
    return;
  }

  state.notice = null;
}

function syncDraftFromFlow(flow: Flow | null): void {
  state.draft.amount = flow?.amount ?? "";
  state.draft.currency = flow?.currency ?? null;
}

function pickPrimaryRoute(snapshot: Pick<AuthData, "activeFlow" | "launchContext">): AppRoute {
  const launchTarget = snapshot.launchContext?.targetRoute ? parseRoute(snapshot.launchContext.targetRoute) : null;
  if (launchTarget) {
    return launchTarget;
  }

  const currentUrlRoute = parseRoute(window.location.pathname);
  if (currentUrlRoute.name !== "home") {
    return currentUrlRoute;
  }

  const explicitEntryRoute = parseRoute(resolveTelegramMiniAppInitialRoute(window.location.pathname, window.location.search));
  if (explicitEntryRoute.name !== "home") {
    return explicitEntryRoute;
  }

  if (snapshot.activeFlow?.status && snapshot.activeFlow.status !== "submitted" && snapshot.activeFlow.status !== "cancelled") {
    return { name: "flow" };
  }

  return { name: "home" };
}

function rememberSubmission(submission: Submission): void {
  state.submissionCache[submission.id] = submission;
  state.currentSubmission = submission;

  if (submission.updatedAt) {
    state.recentSubmission = {
      amount: submission.amount,
      currency: submission.currency,
      status: submission.status,
      submissionId: submission.id,
      updatedAt: submission.updatedAt,
    };
  }
}

function currentSubmissionForRoute(submissionId: string | undefined): Submission | null {
  if (!submissionId) {
    return state.currentSubmission;
  }

  if (state.submissionCache[submissionId]) {
    return state.submissionCache[submissionId];
  }

  if (state.currentSubmission?.id === submissionId) {
    return state.currentSubmission;
  }

  return null;
}

function statusCopy(status: SubmissionStatus): { description: string; title: string } {
  switch (status) {
    case "succeeded":
      return {
        description: "金额与币种已经提交到 Worker 后端，Bot 与 Mini App 会共享同一份结果状态。",
        title: "已完成提交",
      };
    case "processing":
      return {
        description: "后端正在处理这笔提交。你可以稍后刷新，或在历史页继续追踪状态。",
        title: "正在处理",
      };
    case "failed":
      return {
        description: "本次调用未成功完成。保留原金额与币种，可直接重试或回到表单修改。",
        title: "提交失败",
      };
    default:
      return {
        description: "流程状态未知，请刷新页面后重试。",
        title: "状态未知",
      };
  }
}

function friendlyErrorMessage(error: unknown): string {
  if (error instanceof AppError) {
    if (error.code === "INITDATA_MISSING" || error.code === "INITDATA_INVALID" || error.code === "INITDATA_EXPIRED") {
      return "Telegram 身份信息无效，请回到 Bot 重新打开 Mini App。";
    }

    if (error.code === "SESSION_INVALID") {
      return "会话已失效，请回到 Bot 重新进入。";
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "发生未知错误，请稍后重试。";
}

function isBusy(): boolean {
  return state.busy !== "idle";
}

function renderPill(label: string, tone: NoticeTone): string {
  return `<span class="pill pill--${tone}">${escapeHtml(label)}</span>`;
}

function renderNotice(): string {
  if (!state.notice) {
    return "";
  }

  return `
    <section class="notice notice--${state.notice.tone}" aria-live="polite">
      <strong>${escapeHtml(state.notice.text)}</strong>
    </section>
  `;
}

function renderAppBar(): string {
  const showBack = routeNeedsBack(state.route);
  const routeTitle = titleForRoute(state.route);

  return `
    <header class="app-bar">
      <div class="app-bar__leading">
        ${
          showBack
            ? `<button class="icon-button" data-action="back" aria-label="返回上一页">←</button>`
            : `<span class="icon-spacer" aria-hidden="true"></span>`
        }
        <div class="app-bar__copy">
          <span class="app-bar__eyebrow">Telegram Mini App</span>
          <h1 class="app-bar__title">${escapeHtml(routeTitle)}</h1>
        </div>
      </div>
      ${
        state.authState === "ready"
          ? `<button class="ghost-link" data-action="refresh" ${isBusy() ? "disabled" : ""}>刷新</button>`
          : `<span class="icon-spacer" aria-hidden="true"></span>`
      }
    </header>
  `;
}

function renderHeroCard(): string {
  const activeFlow = state.activeFlow;
  const flowLine = activeFlow ? formatMoneyLine(activeFlow.amount, activeFlow.currency) : "输入金额后再选择币种";
  const statusLabel = activeFlow ? flowStatusLabel(activeFlow.status) : "等待创建";
  const tone = activeFlow ? statusTone(activeFlow.status) : "neutral";

  return `
    <section class="hero-card">
      <div class="hero-card__top">
        ${renderPill(statusLabel, tone)}
        <span class="hero-card__meta">金额 → 币种 → 状态/动作</span>
      </div>
      <div class="hero-card__amount">${escapeHtml(flowLine)}</div>
      <p class="hero-card__copy">
        ${escapeHtml(
          activeFlow
            ? "Bot 与 Mini App 共用同一个流程状态。修改金额或币种后，确认页会展示同一份事实源。"
            : "从首页进入金额表单，输入金额、选择币种，然后在确认页一次性提交。",
        )}
      </p>
      <div class="hero-card__chips">
        <span class="metric-chip">移动优先</span>
        <span class="metric-chip">主题跟随 Telegram</span>
        <span class="metric-chip">Cloudflare Pages → Worker API</span>
      </div>
    </section>
  `;
}

function renderHomePage(): string {
  const recent = state.recentSubmission;

  return `
    <div class="page-stack">
      ${renderHeroCard()}
      <section class="card">
        <div class="section-head">
          <div>
            <p class="section-kicker">当前用户</p>
            <h2>${escapeHtml(state.userDisplayName)}</h2>
          </div>
          ${state.activeFlow ? renderPill(flowStatusLabel(state.activeFlow.status), statusTone(state.activeFlow.status)) : ""}
        </div>
        <p class="section-copy">首页会承接 Bot 打开的上下文。若已有进行中的流程，这里会直接显示当前金额与下一步动作。</p>
        <div class="quick-actions">
          <button class="quick-action" data-route="/flow">
            <strong>${state.activeFlow ? "继续当前流程" : "开始填写金额"}</strong>
            <span>进入金额与币种表单</span>
          </button>
          <button class="quick-action" data-route="/history">
            <strong>查看历史记录</strong>
            <span>按时间顺序查看最近提交</span>
          </button>
          <button class="quick-action" data-action="open-bot">
            <strong>返回 Bot</strong>
            <span>从 Telegram 会话恢复入口上下文</span>
          </button>
        </div>
      </section>
      <section class="card">
        <div class="section-head">
          <div>
            <p class="section-kicker">最近结果</p>
            <h2>${recent ? escapeHtml(formatMoneyLine(recent.amount, recent.currency)) : "暂无最近提交"}</h2>
          </div>
          ${recent ? renderPill(submissionStatusLabel(recent.status), statusTone(recent.status)) : ""}
        </div>
        ${
          recent
            ? `
              <div class="detail-list">
                ${renderDetailRow("提交时间", formatTimestamp(recent.updatedAt))}
                ${renderDetailRow("Submission ID", recent.submissionId)}
              </div>
              <button class="secondary-button" data-route="/submission/${encodeURIComponent(recent.submissionId)}">查看详情</button>
            `
            : `<div class="empty-panel"><strong>还没有提交记录</strong><p>完成一次确认提交后，结果页与历史页都会展示同一条记录。</p></div>`
        }
      </section>
    </div>
  `;
}

function renderCurrencyButtons(): string {
  return state.configCurrencies
    .map((currency) => {
      const selected = state.draft.currency === currency;
      return `
        <button
          class="currency-pill ${selected ? "currency-pill--selected" : ""}"
          data-action="select-currency"
          data-currency="${currency}"
          type="button"
        >
          ${currency}
        </button>
      `;
    })
    .join("");
}

function renderFlowPage(): string {
  const flowHint = state.activeFlow ? formatMoneyLine(state.activeFlow.amount, state.activeFlow.currency) : "尚未保存";

  return `
    <div class="page-stack">
      <section class="card card--accent">
        <div class="section-head">
          <div>
            <p class="section-kicker">金额表单</p>
            <h2>金额优先，币种紧随其后</h2>
          </div>
          ${state.activeFlow ? renderPill(flowStatusLabel(state.activeFlow.status), statusTone(state.activeFlow.status)) : ""}
        </div>
        <p class="section-copy">
          输入金额后，使用固定三选币种胶囊完成配对。提交前会自动调用 Worker API 更新当前活跃流程。
        </p>
        <form class="flow-form" data-form="flow">
          <label class="field-block" for="amount-input">
            <span class="field-label">金额</span>
            <input
              id="amount-input"
              name="amount"
              class="amount-input ${state.draft.error ? "amount-input--error" : ""}"
              inputmode="decimal"
              autocomplete="off"
              placeholder="输入金额"
              value="${escapeHtml(state.draft.amount)}"
            />
            <span class="field-help">当前草稿：${escapeHtml(flowHint)}</span>
          </label>
          <div class="field-block">
            <span class="field-label">币种</span>
            <div class="currency-grid">${renderCurrencyButtons()}</div>
            <span class="field-help">固定支持 CNY、USD、USDT，不使用下拉框。</span>
          </div>
          ${
            state.draft.error
              ? `<div class="inline-alert inline-alert--danger" data-role="field-error">${escapeHtml(state.draft.error)}</div>`
              : `<div class="inline-alert inline-alert--hidden" data-role="field-error"></div>`
          }
        </form>
      </section>
      <section class="card">
        <div class="section-head section-head--tight">
          <div>
            <p class="section-kicker">交互说明</p>
            <h2>提交前动作</h2>
          </div>
        </div>
        <div class="detail-list">
          ${renderDetailRow("1", "若无活跃流程，会先 POST /api/v1/flows")}
          ${renderDetailRow("2", "金额保存到 PATCH /flows/{flowId}/amount")}
          ${renderDetailRow("3", "币种保存到 PATCH /flows/{flowId}/currency")}
          ${renderDetailRow("4", "保存成功后进入确认页并读取 /summary")}
        </div>
      </section>
    </div>
  `;
}

function renderConfirmPage(): string {
  const flowId = state.route.name === "confirm" ? state.route.flowId ?? state.activeFlow?.id : state.activeFlow?.id;
  const display = state.summary ? state.summary.displayText : formatMoneyLine(state.activeFlow?.amount, state.activeFlow?.currency);
  const currency = state.summary?.currency ?? state.activeFlow?.currency ?? "--";
  const status = state.activeFlow?.status ?? "awaiting_confirmation";

  if (state.isSummaryLoading) {
    return renderLoaderPage("正在读取确认摘要...");
  }

  if (!flowId || !display || display === "待补充金额与币种") {
    return renderStatePage({
      actions: `<button class="primary-button" data-route="/flow">返回金额表单</button>`,
      description: state.summaryError ?? "当前没有可确认的金额与币种，请先完成表单填写。",
      title: "缺少确认信息",
      tone: "warning",
    });
  }

  return `
    <div class="page-stack">
      <section class="hero-card hero-card--confirm">
        <div class="hero-card__top">
          ${renderPill(flowStatusLabel(status), statusTone(status))}
          <span class="hero-card__meta">统一事实源：GET /flows/${escapeHtml(flowId)}/summary</span>
        </div>
        <div class="hero-card__amount">${escapeHtml(display)}</div>
        <p class="hero-card__copy">确认后会携带 <code>Idempotency-Key</code> 调用 Worker，不会重复提交同一动作。</p>
      </section>
      <section class="card">
        <div class="section-head section-head--tight">
          <div>
            <p class="section-kicker">确认详情</p>
            <h2>提交前最后核对</h2>
          </div>
        </div>
        <div class="detail-list">
          ${renderDetailRow("Flow ID", flowId)}
          ${renderDetailRow("金额", state.summary?.amount ?? state.activeFlow?.amount ?? "--")}
          ${renderDetailRow("币种", String(currency))}
          ${renderDetailRow("下一步", "确认提交到 Worker，随后在结果页查看状态")}
        </div>
      </section>
    </div>
  `;
}

function renderResultPage(): string {
  const submission = state.route.name === "result" ? currentSubmissionForRoute(state.route.submissionId) : state.currentSubmission;

  if (state.isSubmissionLoading) {
    return renderLoaderPage("正在读取提交结果...");
  }

  if (!submission) {
    return renderStatePage({
      actions: `<button class="primary-button" data-route="/history">查看历史记录</button>`,
      description: state.submissionError ?? "暂时没有可展示的提交结果。",
      title: "结果暂不可用",
      tone: "warning",
    });
  }

  const copy = statusCopy(submission.status);

  return `
    <div class="page-stack">
      <section class="state-panel state-panel--${statusTone(submission.status)}">
        <div class="state-panel__icon" aria-hidden="true">${submission.status === "succeeded" ? "✓" : submission.status === "processing" ? "…" : "!"}</div>
        <div class="state-panel__copy">
          <div class="state-panel__top">
            ${renderPill(submissionStatusLabel(submission.status), statusTone(submission.status))}
            <span class="state-panel__meta">${escapeHtml(formatTimestamp(submission.updatedAt))}</span>
          </div>
          <h2>${escapeHtml(copy.title)}</h2>
          <div class="state-panel__amount">${escapeHtml(formatMoneyLine(submission.amount, submission.currency))}</div>
          <p>${escapeHtml(submission.result?.message ?? copy.description)}</p>
        </div>
      </section>
      <section class="card">
        <div class="section-head section-head--tight">
          <div>
            <p class="section-kicker">详情</p>
            <h2>Submission 快照</h2>
          </div>
        </div>
        <div class="detail-list">
          ${renderDetailRow("Submission ID", submission.id)}
          ${renderDetailRow("状态", submissionStatusLabel(submission.status))}
          ${renderDetailRow("更新时间", formatTimestamp(submission.updatedAt))}
          ${submission.failureCode ? renderDetailRow("失败码", submission.failureCode) : ""}
        </div>
      </section>
    </div>
  `;
}

function renderHistoryPage(): string {
  if (state.isHistoryLoading && !state.isHistoryLoaded) {
    return renderLoaderPage("正在读取历史记录...");
  }

  return `
    <div class="page-stack">
      <section class="card">
        <div class="section-head">
          <div>
            <p class="section-kicker">最近提交</p>
            <h2>时间顺序堆叠</h2>
          </div>
          <span class="section-badge">${state.historyItems.length} 条</span>
        </div>
        ${
          state.historyItems.length
            ? `<div class="history-list">${state.historyItems.map((item) => renderHistoryItem(item)).join("")}</div>`
            : `<div class="empty-panel"><strong>暂无历史记录</strong><p>确认提交成功后，这里会按照时间顺序展示最近结果。</p></div>`
        }
      </section>
      ${
        state.historyNextCursor
          ? `<button class="secondary-button secondary-button--wide" data-action="load-more-history" ${state.isHistoryLoading ? "disabled" : ""}>${state.isHistoryLoading ? "加载中..." : "加载更多"}</button>`
          : ""
      }
    </div>
  `;
}

function renderHistoryItem(item: HistoryItem): string {
  return `
    <button class="history-item" data-route="/submission/${encodeURIComponent(item.submissionId)}">
      <div class="history-item__top">
        <strong>${escapeHtml(formatMoneyLine(item.amount, item.currency))}</strong>
        ${renderPill(submissionStatusLabel(item.status), statusTone(item.status))}
      </div>
      <div class="history-item__bottom">
        <span>${escapeHtml(formatTimestamp(item.updatedAt))}</span>
        <span>${escapeHtml(item.submissionId)}</span>
      </div>
    </button>
  `;
}

function renderDetailPage(): string {
  const submission = state.route.name === "detail" ? currentSubmissionForRoute(state.route.submissionId) : state.currentSubmission;

  if (state.isSubmissionLoading) {
    return renderLoaderPage("正在读取详情...");
  }

  if (!submission) {
    return renderStatePage({
      actions: `<button class="primary-button" data-route="/history">返回历史记录</button>`,
      description: state.submissionError ?? "无法读取这条记录的详情。",
      title: "详情暂不可用",
      tone: "warning",
    });
  }

  return `
    <div class="page-stack">
      <section class="hero-card hero-card--detail">
        <div class="hero-card__top">
          ${renderPill(submissionStatusLabel(submission.status), statusTone(submission.status))}
          <span class="hero-card__meta">${escapeHtml(formatTimestamp(submission.updatedAt))}</span>
        </div>
        <div class="hero-card__amount">${escapeHtml(formatMoneyLine(submission.amount, submission.currency))}</div>
        <p class="hero-card__copy">${escapeHtml(submission.result?.message ?? "详情页展示单次 Submission 的结果快照。")}</p>
      </section>
      <section class="card">
        <div class="section-head section-head--tight">
          <div>
            <p class="section-kicker">字段明细</p>
            <h2>与 API 文档一致</h2>
          </div>
        </div>
        <div class="detail-list">
          ${renderDetailRow("Submission ID", submission.id)}
          ${renderDetailRow("金额", submission.amount)}
          ${renderDetailRow("币种", submission.currency)}
          ${renderDetailRow("状态", submissionStatusLabel(submission.status))}
          ${renderDetailRow("更新时间", formatTimestamp(submission.updatedAt))}
          ${submission.flowId ? renderDetailRow("Flow ID", submission.flowId) : ""}
          ${submission.failureCode ? renderDetailRow("失败码", submission.failureCode) : ""}
        </div>
      </section>
    </div>
  `;
}

function renderLoaderPage(message: string): string {
  return `
    <section class="state-panel state-panel--neutral">
      <div class="state-panel__icon state-panel__icon--spinner" aria-hidden="true"></div>
      <div class="state-panel__copy">
        <h2>处理中</h2>
        <p>${escapeHtml(message)}</p>
      </div>
    </section>
  `;
}

function renderStatePage(input: { actions: string; description: string; title: string; tone: NoticeTone }): string {
  return `
    <section class="state-panel state-panel--${input.tone}">
      <div class="state-panel__icon" aria-hidden="true">${input.tone === "danger" ? "!" : input.tone === "success" ? "✓" : "…"}</div>
      <div class="state-panel__copy">
        <h2>${escapeHtml(input.title)}</h2>
        <p>${escapeHtml(input.description)}</p>
      </div>
      <div class="state-panel__actions">${input.actions}</div>
    </section>
  `;
}

function renderDetailRow(label: string, value: string): string {
  return `
    <div class="detail-row">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </div>
  `;
}

function renderActionBar(): string {
  if (state.authState !== "ready") {
    if (state.authState === "error") {
      return `
        <footer class="action-bar">
          <button class="primary-button" data-action="open-bot">回到 Bot 打开</button>
        </footer>
      `;
    }

    return "";
  }

  const disabled = isBusy() ? "disabled" : "";

  switch (state.route.name) {
    case "home":
      return `
        <footer class="action-bar">
          <button class="primary-button" data-route="/flow" ${disabled}>${state.activeFlow ? "继续当前流程" : "开始填写金额"}</button>
          <button class="secondary-button" data-route="/history" ${disabled}>历史记录</button>
        </footer>
      `;
    case "flow":
      return `
        <footer class="action-bar action-bar--split">
          <button class="secondary-button" data-action="cancel-flow" ${!state.activeFlow || isBusy() ? "disabled" : ""}>取消流程</button>
          <button class="primary-button" data-action="save-flow" ${disabled}>${state.busy === "saving" ? "保存中..." : "前往确认"}</button>
        </footer>
      `;
    case "confirm":
      return `
        <footer class="action-bar action-bar--split">
          <button class="secondary-button" data-route="/flow" ${disabled}>返回修改</button>
          <button class="primary-button" data-action="confirm-flow" ${disabled}>${state.busy === "confirming" ? "提交中..." : "确认提交"}</button>
        </footer>
      `;
    case "result":
      return `
        <footer class="action-bar action-bar--split">
          <button class="secondary-button" data-route="/history" ${disabled}>查看历史</button>
          <button
            class="primary-button"
            data-action="${state.currentSubmission?.status === "failed" ? "retry-flow" : "go-home"}"
            ${disabled}
          >
            ${
              state.busy === "retrying"
                ? "重试中..."
                : state.currentSubmission?.status === "failed"
                  ? "重新提交"
                  : "返回首页"
            }
          </button>
        </footer>
      `;
    case "history":
      return `
        <footer class="action-bar action-bar--split">
          <button class="secondary-button" data-route="/" ${disabled}>返回首页</button>
          <button class="primary-button" data-route="/flow" ${disabled}>新建流程</button>
        </footer>
      `;
    case "detail":
      return `
        <footer class="action-bar action-bar--split">
          <button class="secondary-button" data-route="/history" ${disabled}>返回历史</button>
          <button class="primary-button" data-route="/flow" ${disabled}>发起新流程</button>
        </footer>
      `;
    default:
      return "";
  }
}

function renderAuthErrorPage(): string {
  return renderStatePage({
    actions: `
      <button class="primary-button" data-action="open-bot">回到 Bot</button>
      <button class="secondary-button" data-action="refresh">重试</button>
    `,
    description:
      state.authError ?? "Mini App 需要 Telegram initData 才能向 Worker 鉴权。请从 Telegram Bot 的 Web App 按钮重新进入。",
    title: "无法建立会话",
    tone: "danger",
  });
}

function renderLoadingPage(): string {
  return renderLoaderPage("正在校验 Telegram initData 并同步 Worker 会话...");
}

function render(): void {
  document.title = `${titleForRoute(state.route)} · 金额提交流程`;
  syncTelegramBackButton(tg, state.authState === "ready" && routeNeedsBack(state.route));

  let content = "";
  if (state.authState === "loading") {
    content = renderLoadingPage();
  } else if (state.authState === "error") {
    content = renderAuthErrorPage();
  } else {
    switch (state.route.name) {
      case "home":
        content = renderHomePage();
        break;
      case "flow":
        content = renderFlowPage();
        break;
      case "confirm":
        content = renderConfirmPage();
        break;
      case "result":
        content = renderResultPage();
        break;
      case "history":
        content = renderHistoryPage();
        break;
      case "detail":
        content = renderDetailPage();
        break;
      default:
        content = renderHomePage();
    }
  }

  root.innerHTML = `
    <div class="app-shell">
      ${renderAppBar()}
      <main class="app-main">
        ${renderNotice()}
        ${content}
      </main>
      ${renderActionBar()}
    </div>
  `;
}

async function applyRoute(route: AppRoute, options: { replace?: boolean; skipHistoryUpdate?: boolean } = {}): Promise<void> {
  state.route = route;

  if (!options.skipHistoryUpdate) {
    const method = options.replace ? "replaceState" : "pushState";
    window.history[method]({}, "", hrefForRoute(route));
  }

  render();
  await loadRouteData(route);
}

async function refreshSnapshot(options: { silent?: boolean } = {}): Promise<void> {
  if (state.authState !== "ready") {
    return;
  }

  state.busy = options.silent ? "idle" : "refreshing";
  render();

  try {
    const snapshot = await api.bootstrap();
    applySnapshot(snapshot);
    if (!options.silent) {
      setNotice("success", "已从 Worker 刷新当前状态。");
    }
  } catch (error) {
    setNotice("danger", friendlyErrorMessage(error));
  } finally {
    state.busy = "idle";
    render();
  }
}

function applySnapshot(snapshot: Pick<BootstrapData, "activeFlow" | "config" | "recentSubmission" | "user">): void {
  state.userDisplayName = snapshot.user.displayName;
  state.configCurrencies = snapshot.config.currencies.length ? snapshot.config.currencies : state.configCurrencies;
  state.activeFlow = snapshot.activeFlow;
  state.recentSubmission = snapshot.recentSubmission;
  syncDraftFromFlow(snapshot.activeFlow);
}

async function ensureFlow(): Promise<Flow> {
  if (state.activeFlow && state.activeFlow.status !== "submitted" && state.activeFlow.status !== "cancelled") {
    return state.activeFlow;
  }

  const flow = await api.createFlow();
  state.activeFlow = flow;
  syncDraftFromFlow(flow);
  return flow;
}

async function saveFlowDraft(): Promise<void> {
  const amountError = validateAmount(state.draft.amount);
  if (amountError) {
    state.draft.error = amountError;
    render();
    root.querySelector<HTMLInputElement>("#amount-input")?.focus();
    return;
  }

  if (!state.draft.currency) {
    state.draft.error = "请选择币种。";
    render();
    return;
  }

  state.busy = "saving";
  state.draft.error = "";
  clearNotice();
  render();

  try {
    let flow = await ensureFlow();
    const normalizedAmount = normalizeAmountInput(state.draft.amount);
    flow = await api.updateAmount(flow.id, normalizedAmount);
    flow = await api.updateCurrency(flow.id, state.draft.currency);
    state.activeFlow = flow;
    state.summary = {
      amount: normalizedAmount,
      currency: state.draft.currency,
      displayText: formatMoneyLine(normalizedAmount, state.draft.currency),
    };
    state.summaryFlowId = flow.id;
    haptic(tg, "light");
    setNotice("success", "金额与币种已保存，进入确认页。");
    await applyRoute({ name: "confirm", flowId: flow.id });
  } catch (error) {
    haptic(tg, "error");
    setNotice("danger", friendlyErrorMessage(error));
  } finally {
    state.busy = "idle";
    render();
  }
}

async function confirmFlow(): Promise<void> {
  const flowId = state.route.name === "confirm" ? state.route.flowId ?? state.activeFlow?.id : state.activeFlow?.id;
  if (!flowId) {
    setNotice("danger", "没有可提交的流程，请先完成金额与币种填写。");
    render();
    return;
  }

  state.busy = "confirming";
  clearNotice();
  render();

  try {
    const confirmed = await api.confirmFlow(flowId);
    state.activeFlow = confirmed.flow;
    let submission = confirmed.submission;

    try {
      submission = await api.getSubmission(confirmed.submission.id);
    } catch {
      // Keep the confirmation response when detail hydration is unavailable.
    }

    rememberSubmission(submission);
    haptic(tg, submission.status === "failed" ? "error" : submission.status === "processing" ? "warning" : "success");
    setNotice(
      submission.status === "failed" ? "danger" : submission.status === "processing" ? "warning" : "success",
      submission.result?.message ?? statusCopy(submission.status).description,
    );
    await applyRoute({ name: "result", submissionId: submission.id });
  } catch (error) {
    haptic(tg, "error");
    setNotice("danger", friendlyErrorMessage(error));
  } finally {
    state.busy = "idle";
    render();
  }
}

async function retryFlow(): Promise<void> {
  const flowId = state.activeFlow?.id ?? state.currentSubmission?.flowId;
  if (!flowId) {
    setNotice("danger", "当前没有可重试的 Flow ID。");
    render();
    return;
  }

  state.busy = "retrying";
  clearNotice();
  render();

  try {
    const retried = await api.retryFlow(flowId);
    state.activeFlow = retried.flow;
    let submission = retried.submission;

    try {
      submission = await api.getSubmission(retried.submission.id);
    } catch {
      // Keep retry response when detail hydration fails.
    }

    rememberSubmission(submission);
    haptic(tg, submission.status === "failed" ? "error" : submission.status === "processing" ? "warning" : "success");
    setNotice(
      submission.status === "failed" ? "danger" : submission.status === "processing" ? "warning" : "success",
      submission.result?.message ?? statusCopy(submission.status).description,
    );
    await applyRoute({ name: "result", submissionId: submission.id });
  } catch (error) {
    haptic(tg, "error");
    setNotice("danger", friendlyErrorMessage(error));
  } finally {
    state.busy = "idle";
    render();
  }
}

async function cancelFlow(): Promise<void> {
  if (!state.activeFlow) {
    return;
  }

  state.busy = "cancelling";
  clearNotice();
  render();

  try {
    await api.cancelFlow(state.activeFlow.id);
    state.activeFlow = null;
    state.summary = null;
    state.summaryFlowId = null;
    syncDraftFromFlow(null);
    haptic(tg, "warning");
    setNotice("warning", "当前流程已取消。");
    await applyRoute({ name: "home" });
    await refreshSnapshot({ silent: true });
  } catch (error) {
    haptic(tg, "error");
    setNotice("danger", friendlyErrorMessage(error));
  } finally {
    state.busy = "idle";
    render();
  }
}

async function loadHistory(append = false): Promise<void> {
  if (state.authState !== "ready") {
    return;
  }

  state.isHistoryLoading = true;
  render();

  try {
    const data = await api.getHistory(append ? state.historyNextCursor : null, 20);
    state.historyItems = append ? [...state.historyItems, ...data.items] : data.items;
    state.historyNextCursor = data.nextCursor;
    state.isHistoryLoaded = true;
    state.submissionError = null;
  } catch (error) {
    setNotice("danger", friendlyErrorMessage(error));
  } finally {
    state.isHistoryLoading = false;
    render();
  }
}

async function loadSubmission(submissionId: string): Promise<void> {
  if (state.submissionCache[submissionId]) {
    state.currentSubmission = state.submissionCache[submissionId];
    state.submissionError = null;
    render();
    return;
  }

  state.isSubmissionLoading = true;
  state.submissionError = null;
  render();

  try {
    const submission = await api.getSubmission(submissionId);
    rememberSubmission(submission);
    state.submissionError = null;
  } catch (error) {
    state.currentSubmission = null;
    state.submissionError = friendlyErrorMessage(error);
  } finally {
    state.isSubmissionLoading = false;
    render();
  }
}

async function loadSummary(flowId: string): Promise<void> {
  if (state.summary && state.summaryFlowId === flowId) {
    return;
  }

  state.isSummaryLoading = true;
  state.summaryError = null;
  render();

  try {
    const response = await api.getSummary(flowId);
    state.summary = response.summary;
    state.summaryFlowId = flowId;
    state.summaryError = null;
  } catch (error) {
    state.summary = null;
    state.summaryFlowId = null;
    state.summaryError = friendlyErrorMessage(error);
  } finally {
    state.isSummaryLoading = false;
    render();
  }
}

async function loadRouteData(route: AppRoute): Promise<void> {
  if (state.authState !== "ready") {
    return;
  }

  switch (route.name) {
    case "confirm": {
      const flowId = route.flowId ?? state.activeFlow?.id;
      if (flowId) {
        await loadSummary(flowId);
      }
      break;
    }
    case "result": {
      const submissionId = route.submissionId ?? state.recentSubmission?.submissionId;
      if (submissionId) {
        await loadSubmission(submissionId);
      }
      break;
    }
    case "history":
      if (!state.isHistoryLoaded) {
        await loadHistory(false);
      }
      break;
    case "detail":
      await loadSubmission(route.submissionId);
      break;
    default:
      break;
  }
}

function handleBack(): void {
  if (state.route.name === "detail") {
    void applyRoute({ name: "history" });
    return;
  }

  if (state.route.name === "confirm") {
    void applyRoute({ name: "flow" });
    return;
  }

  if (state.route.name === "result") {
    void applyRoute({ name: "home" });
    return;
  }

  if (state.route.name !== "home") {
    void applyRoute({ name: "home" });
  }
}

function clearInlineError(): void {
  const errorNode = root.querySelector<HTMLElement>("[data-role='field-error']");
  if (!errorNode) {
    return;
  }

  errorNode.textContent = "";
  errorNode.classList.add("inline-alert--hidden");
}

async function handleAction(action: string, button: HTMLButtonElement): Promise<void> {
  if (button.disabled) {
    return;
  }

  switch (action) {
    case "back":
      handleBack();
      return;
    case "refresh":
      if (state.authState === "ready") {
        await refreshSnapshot();
      } else {
        window.location.reload();
      }
      return;
    case "open-bot":
      if (!ENV.botUsername) {
        setNotice("warning", "缺少 VITE_BOT_USERNAME，无法跳回 Bot。");
        render();
        return;
      }
      openBotLink(ENV.botUsername, tg);
      return;
    case "save-flow":
      await saveFlowDraft();
      return;
    case "cancel-flow":
      await cancelFlow();
      return;
    case "confirm-flow":
      await confirmFlow();
      return;
    case "retry-flow":
      await retryFlow();
      return;
    case "go-home":
      await applyRoute({ name: "home" });
      return;
    case "load-more-history":
      await loadHistory(true);
      return;
    case "select-currency": {
      const currency = button.dataset.currency as Currency | undefined;
      if (currency) {
        state.draft.currency = currency;
        state.draft.error = "";
        clearInlineError();
        render();
      }
      return;
    }
    default:
      return;
  }
}

root.addEventListener("click", (event) => {
  const target = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>("button, a") : null;
  if (!target) {
    return;
  }

  if (target instanceof HTMLButtonElement) {
    const action = target.dataset.action;
    const route = target.dataset.route;

    if (route) {
      event.preventDefault();
      void applyRoute(parseRoute(route));
      return;
    }

    if (action) {
      event.preventDefault();
      void handleAction(action, target);
    }
  }
});

root.addEventListener("submit", (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form || form.dataset.form !== "flow") {
    return;
  }

  event.preventDefault();
  void saveFlowDraft();
});

root.addEventListener("input", (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input || input.name !== "amount") {
    return;
  }

  const nextValue = sanitizeAmountInput(input.value);
  if (nextValue !== input.value) {
    input.value = nextValue;
  }

  state.draft.amount = nextValue;
  state.draft.error = "";
  clearInlineError();
});

window.addEventListener("popstate", () => {
  void applyRoute(parseRoute(window.location.pathname), { skipHistoryUpdate: true });
});

function handleThemeChange(theme: TelegramThemeParams | undefined, scheme: ColorScheme): void {
  applyTheme(theme, scheme);
  syncTelegramChrome(tg);
  render();
}

async function bootstrapApp(): Promise<void> {
  initializeTelegram(tg, {
    onBack: handleBack,
    onThemeChange: handleThemeChange,
  });

  if (!tg?.initData) {
    state.authState = "error";
    state.authError = "未检测到 Telegram initData。请从 Bot 的 Web App 按钮重新打开。";
    render();
    return;
  }

  if (ENV.configError) {
    state.authState = "error";
    state.authError = ENV.configError;
    render();
    return;
  }

  render();

  try {
    const auth = await api.authenticate({
      client: {
        appVersion: ENV.appVersion,
        platform: tg.platform ?? "web",
      },
      initData: tg.initData,
      launchToken: resolveLaunchToken(tg),
    });

    state.accessToken = auth.accessToken;
    api.setAccessToken(auth.accessToken);
    state.authState = "ready";
    state.authError = null;
    applySnapshot(auth);
    const nextRoute = pickPrimaryRoute(auth);
    await applyRoute(nextRoute, { replace: true });
  } catch (error) {
    state.authState = "error";
    state.authError = friendlyErrorMessage(error);
    render();
  }
}

void bootstrapApp();
